import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Compass, X, Send, Loader2 } from "lucide-react";
import { API } from "../lib/api";

const SUGGESTIONS = [
  "Come imposto una AI policy per il mio board?",
  "Cos'è un Fractional CIO?",
  "Da dove parto con l'Enterprise Risk Management?",
];

export const BoardCompass = () => {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => `compass-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Sono AI Board Compass™. Fammi una domanda su AI governance, rischio o trasformazione digitale." },
  ]);
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const resp = await fetch(`${API}/board-compass/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Si è verificato un errore. Riprova." };
        return copy;
      });
    }
    setStreaming(false);
  };

  return (
    <>
      <motion.button
        data-testid="compass-toggle"
        onClick={() => setOpen((o) => !o)}
        initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 2, type: "spring" }}
        className="fixed bottom-6 right-6 z-[65] w-14 h-14 rounded-full bg-[var(--petrol)] text-white flex items-center justify-center shadow-xl hover:scale-105 transition-transform"
      >
        <AnimatePresence mode="wait">
          {open ? <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ opacity: 0 }}><X strokeWidth={1.5} /></motion.span>
                : <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ opacity: 0 }}><Compass strokeWidth={1.5} /></motion.span>}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="compass-panel"
            initial={{ opacity: 0, y: 20, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-[65] w-[calc(100vw-3rem)] sm:w-[400px] h-[540px] max-h-[75vh] bg-white border border-[var(--line)] shadow-2xl flex flex-col"
          >
            <div className="bg-[var(--petrol)] text-white p-5">
              <div className="flex items-center gap-2 font-serif text-xl"><Compass size={18} strokeWidth={1.5} /> AI Board Compass™</div>
              <p className="text-xs text-white/60 mt-1">Assistente di governance in tempo reale</p>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} data-testid={`compass-msg-${m.role}`}
                  className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user" ? "ml-auto bg-[var(--petrol)] text-white" : "bg-[var(--paper-2)] text-[var(--ink)]"
                  }`}>
                  {m.content || (streaming && i === messages.length - 1 ? <Loader2 size={14} className="animate-spin" /> : "")}
                </div>
              ))}
              {messages.length === 1 && (
                <div className="pt-2 space-y-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button key={i} data-testid={`compass-suggestion-${i}`} onClick={() => send(s)}
                      className="block w-full text-left text-xs border border-[var(--line)] px-3 py-2 text-[var(--ink-soft)] hover:border-[var(--petrol)] hover:text-[var(--petrol)] transition-[color,border-color]">
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-[var(--line)] flex gap-2">
              <input data-testid="compass-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Scrivi una domanda…"
                className="flex-1 text-sm px-3 py-2 border border-[var(--line)] focus:border-[var(--petrol)] outline-none" />
              <button data-testid="compass-send" disabled={streaming} className="bg-[var(--petrol)] text-white px-4 disabled:opacity-50">
                {streaming ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} strokeWidth={1.5} />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
